/**
 * Setup → Price matrix.
 *
 * A matrix is a floor template with **no scope**: one reference ACRISS
 * priced per duration band, the others written as an adjustment on it.
 * Binding one to a franchise, an insurance, an office and a date range
 * happens in Price Limits, not here — this tab only knows about cars
 * and days, which is what makes the same grid reusable.
 *
 * Why this exists: a floor used to be one row carrying a whole scope,
 * so pricing a fleet meant a row per ACRISS per duration, repeated for
 * every franchise, insurance, office and season. Thibault asked to
 * write the grid once (2026-09-30).
 *
 * Two kinds of work happen on this screen and they are not the same
 * size. **Deciding the bands** happens once, when the matrix is born,
 * and rarely again. **Filling prices** is the daily work. The first two
 * versions mixed them — a permanent block of boundary inputs, then
 * split/merge buttons inside every column header — and both put the
 * rare, structural edit in the way of the frequent one. Now the grid
 * shows bands as plain labels and one button opens an editor where the
 * whole 1–30 continuum is visible (Curro, 2026-10-01).
 *
 * Visually it follows Thibault's mock — numbered sections, ACRISS as
 * pills, a tinted table head, heavy labels, tabular figures — in the
 * app's own palette and components. Type is set in px here because the
 * app theme's rem scale on a 13 px root renders captions below
 * legibility, and Curro reads the whole app as too small (2026-10-01).
 */
import React, { useEffect, useMemo, useState } from 'react'
import {
	Autocomplete, Box, Button, Chip, IconButton, Menu, MenuItem, Select, Stack,
	Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
	TextField, Typography,
} from '@mui/material'
import { dsColorNew } from '@palette'
import { alpha } from '@mui/material/styles'
import {
	AppDialog, AppDialogHeader, AppDialogBody, AppDialogFooter, AppDialogCancel, AppValueChip, AppNote,
	AppBulkEditAction, AppFilterChip, AppStartHint, secondaryButtonSx,
} from '@components'
import { Surface } from 'Pages/DsRepricing/Setup/@components/Surface'
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { setupScopeSx } from 'Pages/DsRepricing/Setup/@components/setupScopeSx'
import { Add as AddIcon, DeleteOutline as DeleteIcon, ContentCopyOutlined as CopyIcon, TuneOutlined as TuneIcon, EditOutlined as EditIcon, MoreHorizOutlined as MoreIcon } from 'Pages/DsRepricing/@components/icons'
import { exactCopy } from 'Pages/DsRepricing/Setup/@services/matrixAdjust'
import SectionCard from 'Pages/DsRepricing/@components/SectionCard'
import useConfirm from 'Pages/DsRepricing/@components/useConfirm'
import { inputSx, headCellSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import { useSetupCollection } from 'Pages/DsRepricing/Setup/@services'
import { useCategories } from 'Pages/DsRepricing/Setup/@services/useCategories'
import { priceMatrices as adapter } from 'Pages/DsRepricing/Setup/@services/adapters'
import { setupApi } from 'Pages/DsRepricing/Setup/@services/api'
import useToast from '@hooks/useToast'
import DuplicateDialog from './DuplicateDialog'
import UnsavedChip from 'Pages/DsRepricing/Setup/@components/UnsavedChip'

// ─── Local scale ─────────────────────────────────────────────────────────
// px, not rem: the app theme's rem variants on a 13 px root put captions
// under 10 px. Nothing here renders below 12. The app's scale: body 14,
// labels 13, captions 12 (titles come from the cards).
const T = { body: 14, label: 13, caption: 12 }
const W = { heavy: 700, bold: 600, medium: 500 }

/** Where the preview stops. The tail band covers everything past it. */
const PREVIEW_DAYS = 30

const money = (n) => (n == null ? '—'
	: new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2,
	                                   maximumFractionDigits: 2 }).format(n))

/** Short on purpose: the axis is named once per table, not per band. */
const bandLabel = (b) => (b.to == null ? `${b.from}+ days` : `${b.from}–${b.to} days`)

/**
 * Bands from breakpoints. `[7, 15]` becomes 1–7, 8–15, 16–∞.
 *
 * The open tail falls out of the cuts rather than being its own
 * control: the last day somebody names is where the monthly band starts.
 */
function bandsFromCuts(cuts) {
	const sorted = [...new Set(cuts.map(Number))]
		.filter(n => Number.isInteger(n) && n >= 1 && n <= PREVIEW_DAYS)
		.sort((a, b) => a - b)
	const out = []
	let start = 1
	for (const cut of sorted) { out.push({ from: start, to: cut }); start = cut + 1 }
	out.push({ from: start, to: null })
	return out
}

const cutsFromBands = (bands) => (bands || []).filter(b => b.to != null).map(b => b.to)

/** The partitions people actually use. One click instead of four inputs. */
const QUICK_PICKS = [
	{ label: '1–7 · 8–30 · 30+',               cuts: [7, 30] },
	{ label: '1–3 · 4–7 · 8–14 · 15–29 · 30+',  cuts: [3, 7, 14, 29] },
	{ label: 'Weekly',                          cuts: [7, 14, 21, 28] },
	{ label: 'Single band',                     cuts: [] },
]

/** An empty matrix that is already valid enough to show something. */
const blank = () => ({
	name: '', base_acriss: '',
	base: [{ from: 1, to: 7, price: 40 }, { from: 8, to: 30, price: 32 },
	       { from: 31, to: null, price: 28 }],
	rules: [],
})

// ─── Small pieces, Thibault's vocabulary in the app's palette ────────────

/** An ACRISS as a pill. The reference one is tinted apart. */
function Code({ children, base = false }) {
	return (
		<Box component="span" sx={{
			display: 'inline-block', px: 1.5, py: 0.5, borderRadius: 999,
			fontSize: T.label, fontWeight: W.heavy, letterSpacing: '0.03em',
			fontFamily: 'inherit',
			bgcolor: base ? dsColorNew.successBg : dsColorNew.pillActiveBg,
			color:   base ? dsColorNew.successText : dsColorNew.appBlue,
			border: '1px solid', borderColor: base ? alpha(dsColorNew.successBar, 0.4) : dsColorNew.noteInfoBorder,
		}}>
			{children}
		</Box>
	)
}

/** A small uppercase heading over a group of controls, in the mock's weight. */
const Label = ({ children, sx = {} }) => (
	<Typography sx={{ fontSize: T.caption, fontWeight: W.heavy, letterSpacing: '0.06em',
	                  textTransform: 'uppercase', color: dsColorNew.textMuted, mb: 1, ...sx }}>
		{children}
	</Typography>
)

/**
 * A table head cell that is readable. The global theme paints head text
 * in a secondary tone and a sticky head picks up a background that
 * fights it; set both here, like every other table in this section does.
 */
/** The first column stays put while the bands scroll under it: without
 *  it, scrolling right loses which ACRISS each row is. Needs an opaque
 *  background of its own — a sticky cell is painted over otherwise. */
const stickyCol = (bg = dsColorNew.white) => ({
	position: 'sticky', left: 0, zIndex: 2, bgcolor: bg,
	borderRight: '1px solid', borderRightColor: dsColorNew.grayBorder,
})

function Th({ children, sx = {}, ...rest }) {
	return (
		<TableCell {...rest} sx={{
			...headCellSx, whiteSpace: 'nowrap', py: 1.75,
			...sx,
		}}>
			{children}
		</TableCell>
	)
}

const Note = ({ children, sx = {} }) => (
	<Typography sx={{ fontSize: T.label, color: dsColorNew.textMuted, lineHeight: 1.55, ...sx }}>
		{children}
	</Typography>
)

/**
 * The height of a field belongs to the control, not to the `<input>`
 * inside it: MUI's outlined input adds its own padding around the
 * element, so a height set here comes out ~20 px taller than a `Select`
 * with the same number on its root. That is why the number boxes towered
 * over the unit pickers beside them (Curro, 2026-10-03).
 */
const fieldH = (h = 38) => ({ '& .MuiOutlinedInput-root': { height: h } })
const numberInput = { fontSize: T.body, fontWeight: W.bold }
const textInput = { fontSize: T.body }

// ─── Tab ─────────────────────────────────────────────────────────────────

export default function PriceMatricesTab() {
	const [confirm, confirmDialog] = useConfirm()
	const { showToast } = useToast()
	const cat = useCategories()
	const [matrices, setMatrices, sync] = useSetupCollection('priceMatrices')
	const [selectedId, setSelectedId] = useState(null)
	const [duplicating, setDuplicating] = useState(null)

	// The first save swaps the local id for the server's. Without
	// following it, the row being edited stops existing the moment it
	// is saved and the panel empties itself mid-sentence.
	useEffect(() => {
		if (!selectedId) return
		const moved = sync.remap?.get(selectedId)
		if (moved) setSelectedId(moved)
	}, [sync.remap, selectedId])

	// What `MatrixList` shows as "not saved" per matrix: the server's
	// refusal, its silence, or the name/ACRISS a new one still lacks —
	// which it held in silence until now (CU-869ffwj7a).
	const marks = useMemo(() => {
		const out = new Map()
		for (const m of matrices) {
			const refused = sync.refused?.get(m.id)
			const stalled = sync.stalled?.has(m.id)
			const reason = sync.held?.has(m.id)
				? 'Give it a name and a reference ACRISS and it saves itself.' : null
			if (refused || stalled || reason) out.set(m.id, { refused, stalled, reason })
		}
		return out
	}, [matrices, sync.refused, sync.stalled, sync.held])

	const selected = matrices.find(m => m.id === selectedId) || null

	const patch = (changes) => setMatrices(all => all.map(
		m => (m.id === selectedId ? { ...m, ...changes } : m)))

	const add = () => {
		const draft = { ...blank(), id: `new-${Date.now()}` }
		setMatrices(all => [draft, ...all])
		setSelectedId(draft.id)
	}

	/**
	 * The copy joins the list exactly like a new matrix does — the hook
	 * diffuses it on its own schedule. A POST from the dialog would
	 * leave `serverRef` behind and the next sync would create a second
	 * one; and the `new-` prefix is load-bearing, `askImpact` reads it
	 * to not ask the server about a matrix it has never seen.
	 */
	const createCopy = (copy) => {
		const draft = { ...copy, id: `new-${Date.now()}` }
		setMatrices(all => [draft, ...all])
		setSelectedId(draft.id)
		showToast.success(`"${draft.name}" created. Attach it to a scope in Price limits.`)
	}

	/**
	 * One click, no dialog: the copy Thibault asked for — every value of
	 * the original, opened in the editor to change one band (2026-10-08,
	 * "como Duplicate report en los rate shops"). Same copy the dialog
	 * makes with zero adjustments, by construction (`exactCopy`); same
	 * toast, because what matters to say is that it is bound to no scope.
	 */
	const duplicateToEdit = (m) => createCopy(exactCopy(m, matrices.map(x => x.name)))

	const remove = async (m) => {
		const ok = await confirm({
			title: 'Delete this matrix?',
			body: `"${m.name || 'Untitled'}" will stop setting floors for every scope `
			    + 'bound to it. The server refuses if any still are.',
			confirmLabel: 'Delete',
		})
		if (!ok) return
		setMatrices(all => all.filter(x => x.id !== m.id))
		if (selectedId === m.id) setSelectedId(null)
	}

	return (
		<>
			{confirmDialog}
			{/* `minmax(0, 1fr)`, not `1fr`: a grid column's default minimum is
			    its content, so a wide table pushed the column — and with it the
			    whole page — instead of scrolling inside its own container
			    (Curro, 2026-10-03: "un scroll horizontal brutal"). */}
			<Box sx={{ display: 'grid', // 320, not 300: with three row icons the name had ~142 px and
			           // "Winter 26/27 (copy)" — the very name the gesture makes — was
			           // cut (UX gate, 2026-10-08).
			           gridTemplateColumns: { xs: '1fr', md: '320px minmax(0, 1fr)' },
			           gap: 2.5, alignItems: 'start', minWidth: 0 }}>
				<MatrixList matrices={matrices} selectedId={selectedId}
				            onSelect={setSelectedId} onAdd={add} onDelete={remove}
				            onDuplicateToEdit={duplicateToEdit}
				            onDuplicate={setDuplicating}
				            marks={marks}
				            loading={sync.loading}/>
				{selected
					? <MatrixEditor key={selected.id} matrix={selected} patch={patch}
					                categories={cat}/>
					: <Empty onAdd={add}/>}
			</Box>
			{duplicating && (
				<DuplicateDialog matrix={duplicating}
				                 existingNames={matrices.map(m => m.name)}
				                 onCreate={createCopy}
				                 onClose={() => setDuplicating(null)}/>
			)}
		</>
	)
}

// Nothing picked yet: the app's start hint, with the one action that gets the tab going
function Empty({ onAdd }) {
	return (
		<Surface sx={{ pb: 5 }}>
			<AppStartHint icon="table-cells" title="No matrix selected"
			              description="A matrix is a grid of floors with no scope. You attach it to a franchise, an office and a date range over in Price limits."
			              sx={{ pt: 5, pb: 2.5 }}/>
			<Box sx={{ textAlign: 'center' }}>
				<AppBulkEditAction startIcon={<AddIcon/>} onClick={onAdd}>
					New matrix
				</AppBulkEditAction>
			</Box>
		</Surface>
	)
}

function MatrixList({ matrices, selectedId, onSelect, onAdd, onDelete, onDuplicate, onDuplicateToEdit, marks, loading }) {
	return (
		<Surface>
			<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
				<Label sx={{ mb: 0 }}>Matrices</Label>
				<Button size="small" startIcon={<AddIcon/>} onClick={onAdd} sx={{ fontSize: T.label }}>
					New
				</Button>
			</Stack>
			{loading && <Note>Loading…</Note>}
			{!loading && matrices.length === 0 && <Note>None yet.</Note>}
			<Stack gap={0.75}>
				{matrices.map(m => {
					const on = m.id === selectedId
					const mark = marks?.get(m.id)
					return (
						<Box key={m.id} onClick={() => onSelect(m.id)}
						     sx={{
							     display: 'flex', alignItems: 'center', gap: 1,
							     px: 1.5, py: 1.25, borderRadius: 1.5, cursor: 'pointer',
							     border: '1px solid',
							     borderColor: on ? dsColorNew.appBlue : dsColorNew.grayBorder,
							     bgcolor: on ? dsColorNew.pillActiveBg : dsColorNew.white,
							     '&:hover': { borderColor: on ? dsColorNew.appBlue : dsColorNew.noteInfoBorder },
						     }}>
							<Box sx={{ minWidth: 0, flex: 1 }}>
								<Typography noWrap title={m.name || 'Untitled'}
								            sx={{ fontWeight: W.bold, fontSize: T.body, color: dsColorNew.textTitle }}>
									{m.name || 'Untitled'}
								</Typography>
								<Typography sx={{ fontSize: T.caption, color: dsColorNew.textMuted, mt: 0.25 }}>
									{m.base_acriss || 'no reference'} · {(m.rules || []).length + 1} ACRISS
								</Typography>
							</Box>
							{mark && <UnsavedChip {...mark} sx={{ mr: 0 }}/>}
							{/* ⧉ means "duplicate, no dialog" everywhere else in the new
							    line (Price limits, Schedule); here it used to open the
							    adjustments dialog, which is why Thibault never found the
							    plain copy. The dialog moved to the sliders (UX, 2026-10-08).
							    Neither copy is offered on an unsaved matrix: there is
							    nothing on the server to copy, and copying an unsaved row
							    makes two unsaved rows. */}
							{!mark && (<>
							<IconButton aria-label="Duplicate to edit" size="small"
							            title="Duplicate to edit"
							            onClick={(e) => { e.stopPropagation(); onDuplicateToEdit(m) }}
							            sx={{ color: dsColorNew.textMuted,
							                  '&:hover': { color: dsColorNew.appBlue, bgcolor: dsColorNew.pillActiveBg } }}>
								<CopyIcon fontSize="small"/>
							</IconButton>
							<IconButton aria-label="Duplicate with adjustments" size="small"
							            title="Duplicate with adjustments"
							            onClick={(e) => { e.stopPropagation(); onDuplicate(m) }}
							            sx={{ color: dsColorNew.textMuted,
							                  '&:hover': { color: dsColorNew.appBlue, bgcolor: dsColorNew.pillActiveBg } }}>
								<TuneIcon fontSize="small"/>
							</IconButton>
							</>)}
							<IconButton aria-label="Delete this matrix" size="small"
							            title="Delete this matrix"
							            onClick={(e) => { e.stopPropagation(); onDelete(m) }}
							            sx={{ color: dsColorNew.textMuted,
							                  '&:hover': { color: dsColorNew.dangerRed, bgcolor: dsColorNew.dangerBg } }}>
								<DeleteIcon fontSize="small"/>
							</IconButton>
						</Box>
					)
				})}
			</Stack>
		</Surface>
	)
}

// ─── Editor ──────────────────────────────────────────────────────────────

function MatrixEditor({ matrix, patch, categories }) {
	const [impact, setImpact] = useState(null)
	const [bandsOpen, setBandsOpen] = useState(false)

	const sipps = categories.allSipps || []
	const bands = matrix.base || []
	const linked = (matrix.rules || []).map(r => r.acriss)
	const hasBase = !!matrix.base_acriss

	/** Re-cut every row at once: the columns are shared by definition. */
	const setCuts = (cuts) => {
		const next = bandsFromCuts(cuts)
		const carry = (old, fallback) => next.map(b => {
			const previous = (old || []).find(o => o.from <= b.from
				&& (o.to == null || o.to >= b.from))
			const { from, to, ...rest } = previous || {}
			return { ...b, ...(previous ? rest : fallback) }
		})
		patch({
			base: carry(matrix.base, { price: 0 }),
			rules: (matrix.rules || []).map(r => ({
				...r, bands: carry(r.bands, { value: 0, unit: 'euro' }),
			})),
		})
	}

	const setLinked = (codes) => {
		const kept = (matrix.rules || []).filter(r => codes.includes(r.acriss))
		const added = codes
			.filter(c => !kept.some(r => r.acriss === c))
			.map(c => ({ acriss: c, bands: bands.map(b => ({ ...b, value: 0, unit: 'euro' })) }))
		patch({ rules: [...kept, ...added] })
	}

	const setBase = (code) => {
		// An ACRISS is either the reference or written against it, never
		// both: picking one that was linked moves it out of the rules.
		patch({ base_acriss: code || '',
		        rules: (matrix.rules || []).filter(r => r.acriss !== code) })
	}

	const setBaseBand = (i, price) => patch({
		base: matrix.base.map((b, j) => (j === i ? { ...b, price } : b)),
	})

	const setRuleBand = (acriss, i, changes) => patch({
		rules: matrix.rules.map(r => (r.acriss !== acriss ? r : {
			...r, bands: r.bands.map((b, j) => (j === i ? { ...b, ...changes } : b)),
		})),
	})

	// Row shortcuts. Most adjustments are flat across durations, so a
	// row of four bands was eight controls to say one thing. Each of
	// these says it once (Curro, 2026-10-01).
	const fillBase = (price) => patch({
		base: matrix.base.map(b => ({ ...b, price })),
	})
	const fillRule = (acriss, value, unit) => patch({
		rules: matrix.rules.map(r => (r.acriss !== acriss ? r : {
			...r, bands: r.bands.map(b => ({ ...b, value, unit })),
		})),
	})
	// Copies values and units band by band — the rows share the same
	// cuts by construction, so positions line up.
	const copyRule = (toAcriss, fromAcriss) => {
		const src = matrix.rules.find(r => r.acriss === fromAcriss)
		if (!src) return
		patch({
			rules: matrix.rules.map(r => (r.acriss !== toAcriss ? r : {
				...r, bands: r.bands.map((b, j) => ({
					...b, value: src.bands[j]?.value ?? 0, unit: src.bands[j]?.unit || 'euro',
				})),
			})),
		})
	}

	/**
	 * One day's base floor, straight from the preview. The band covering
	 * it is split so the day becomes a band of its own — which is what a
	 * single-day override already IS in this model. It does not re-merge
	 * if set back to its neighbours' value: fusing bands behind the
	 * operator's back is what made the old cut list untrustworthy.
	 */
	const setDayPrice = (lor, price) => {
		const out = []
		for (const b of matrix.base || []) {
			const covers = lor >= b.from && (b.to == null || lor <= b.to)
			if (!covers) { out.push(b); continue }
			if (b.from === lor && b.to === lor) { out.push({ ...b, price }); continue }
			if (b.from < lor) out.push({ ...b, to: lor - 1 })
			out.push({ from: lor, to: lor, price })
			if (b.to == null || b.to > lor) out.push({ ...b, from: lor + 1 })
		}
		patch({ base: out })
	}

	const preview = useMemo(() => {
		const codes = [matrix.base_acriss, ...linked].filter(Boolean)
		return Array.from({ length: PREVIEW_DAYS }, (_, i) => {
			const lor = i + 1
			const row = { lor, prices: {} }
			for (const code of codes) row.prices[code] = adapter.floorFor(matrix, code, lor)
			return row
		})
	}, [matrix, linked.join('|')])

	const floors = preview.reduce((n, r) => n + Object.values(r.prices).filter(v => v != null).length, 0)
	const negatives = preview.reduce((n, r) => n + Object.values(r.prices).filter(v => v != null && v < 0).length, 0)
	const gaps = preview.some(r => Object.values(r.prices).some(v => v == null))

	const askImpact = async () => {
		if (!matrix.id || String(matrix.id).startsWith('new-')) return
		try {
			const scopes = await setupApi.matrixUsage(matrix.id)
			if (scopes > 1) setImpact(scopes)
		} catch { /* the count is a courtesy; never block the edit on it */ }
	}

	return (
		// `minWidth: 0` all the way down: a flex item's default minimum is
		// its content, so one wide table widened the editor, the grid
		// column and finally the page. The cap has to hold at every link
		// of the chain or the scroll escapes again.
		<Stack gap={2.5} sx={{ minWidth: 0 }}>
			<SectionCard step={1} title="The matrix"
			             subtitle="Its name, and the ACRISS every other price is written against.">
				<Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
					<TextField label="Name" size="small" sx={{ ...inputSx, flex: 1 }}
					           value={matrix.name || ''} onBlur={askImpact}
					           onChange={(e) => patch({ name: e.target.value })}
					           inputProps={{ style: textInput }}/>
					<Autocomplete
						size="small" sx={{ ...inputSx, width: 280 }}
						options={sipps} value={matrix.base_acriss || null}
						onChange={(_, v) => setBase(v)}
						loading={categories.loading}
						renderInput={(p) => <TextField {...p} label="Reference ACRISS" required
						                               inputProps={{ ...p.inputProps, style: textInput }}/>}/>
				</Stack>
				{categories.error && (
					<AppNote severity="warning" sx={{ mt: 1.5 }}>{categories.error}</AppNote>
				)}
				{/* The basis is stated as "X — not Y" on purpose: it is the
				    negation that makes the author notice if he meant the other
				    one. Thibault confirmed his floors are retail on 2026-10-05;
				    which retail — the broker's shelf price — is the engine's
				    choice pending his word (plan §4.1). */}
				<Note sx={{ mt: 1.5 }}>
					Floors are per rental day, on the broker's <strong>shelf price</strong> — what
					the customer pays the broker, the same price the market is compared against.
					Not the price that reaches the PMS after the markup comes off.
					Bands run on length of rental, not on calendar dates.
				</Note>
			</SectionCard>

			<SectionCard step={2} title="Prices by band"
			             subtitle="Bands are lengths of rental, in days. The reference carries the price; each related ACRISS is an adjustment on it, per band.">
				<Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems="flex-start">
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Autocomplete
							multiple size="small" sx={inputSx}
							disabled={!hasBase}
							options={sipps.filter(s => s !== matrix.base_acriss)}
							value={linked} onChange={(_, v) => setLinked(v)}
							loading={categories.loading}
							renderTags={(value, getTagProps) => value.map((code, i) => (
								<Chip {...getTagProps({ index: i })} key={code} label={code} size="small"
								      sx={{ fontWeight: W.bold, fontSize: T.label, height: 28 }}/>
							))}
							renderInput={(p) => <TextField {...p} label="Related ACRISS"
							                               placeholder={hasBase ? 'Add ACRISS…' : ''}
							                               helperText={hasBase ? ' '
							                                 : 'Pick the reference ACRISS first — the others are written against it.'}
							                               FormHelperTextProps={{ sx: { fontSize: T.caption, mx: 0 } }}
							                               inputProps={{ ...p.inputProps, style: textInput }}/>}/>
					</Box>
					<Button variant="outlined" startIcon={<EditIcon/>} onClick={() => setBandsOpen(true)}
					        disabled={!hasBase}
					        sx={{ ...secondaryButtonSx, whiteSpace: 'nowrap', flexShrink: 0 }}>
						Edit bands · {bands.length}
					</Button>
				</Stack>

				{hasBase && (
					<RelationTable matrix={matrix} bands={bands}
					               onBase={setBaseBand} onRule={setRuleBand}
					               onFillBase={fillBase} onFillRule={fillRule} onCopyRule={copyRule}/>
				)}
			</SectionCard>

			<SectionCard step={3} title="Resulting floors"
			             subtitle={`One row per length of rental, 1 to ${PREVIEW_DAYS} days. The reference column is editable here, one length at a time.`}>
				<Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mb: 2 }}>
					{[`${bands.length} bands`, `${linked.length + (hasBase ? 1 : 0)} ACRISS`, `${floors} floors`]
						.map(l => <AppValueChip key={l} tone="accent" sx={{ fontSize: 12.5, fontWeight: 500 }}>{l}</AppValueChip>)}
					{gaps && <AppValueChip tone="warning" sx={{ fontSize: 12.5 }}>Some days have no floor</AppValueChip>}
				</Stack>
				{negatives > 0 && (
					<AppNote severity="error" sx={{ mb: 2 }}>
						{negatives} of these come out negative. An adjustment is below the
						reference price by more than the reference itself.
					</AppNote>
				)}
				{hasBase
					? <PreviewTable rows={preview} base={matrix.base_acriss} linked={linked}
					                onDayPrice={setDayPrice}/>
					: <Note>Nothing to show until the reference ACRISS is chosen.</Note>}
			</SectionCard>

			<BandEditorDialog open={bandsOpen} bands={bands}
			                  onClose={() => setBandsOpen(false)}
			                  onApply={(cuts) => { setCuts(cuts); setBandsOpen(false) }}/>

			<AppDialog open={impact != null} onClose={() => setImpact(null)} maxWidth="xs" paperSx={setupScopeSx}>
				<AppDialogHeader icon="circle-info" title={`This matrix prices ${impact} scopes`} onClose={() => setImpact(null)}/>
				<AppDialogBody sx={dialogBodySx}>
					<Note sx={{ fontSize: T.body }}>
						Every change here moves the floors in all of them at once. That is
						what a template is for, but it is worth knowing before you save.
					</Note>
				</AppDialogBody>
				<AppDialogFooter>
					<AppBulkEditAction onClick={() => setImpact(null)}>
						Understood
					</AppBulkEditAction>
				</AppDialogFooter>
			</AppDialog>
		</Stack>
	)
}

// ─── The grid ────────────────────────────────────────────────────────────

// Exported so a render smoke test can mount it: the crash of
// 2026-10-03 shipped because nothing in the pipeline renders.
export function RelationTable({ matrix, bands, onBase, onRule, onFillBase, onFillRule, onCopyRule }) {
	return (
		<TableContainer sx={{ mt: 2.5, border: '1px solid', borderColor: dsColorNew.grayBorder,
		                      borderRadius: 1.5, overflowX: 'auto', maxWidth: '100%' }}>
			<Table size="small" sx={{ '& td, & th': { py: 1 } }}>
				<TableHead>
					<TableRow>
						<Th sx={{ minWidth: 132, ...stickyCol(dsColorNew.tableHead), zIndex: 3 }}>ACRISS</Th>
						{bands.map(b => (
							<Th key={b.from} sx={{ minWidth: 148 }}>{bandLabel(b)}</Th>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					<TableRow>
						{/* One line, not five. The three row shortcuts used to be
						    stacked chips, which made every row ~290 px tall and wrapped
						    their own labels through their borders (Curro, 2026-10-03).
						    They live behind the ⋯ now; nothing was dropped. */}
						<TableCell sx={{ ...stickyCol() }}>
							<Stack direction="row" alignItems="center" gap={0.75}>
								<Code base>{matrix.base_acriss}</Code>
								<Box flex={1}/>
								<RowMenu label={`Shortcuts for ${matrix.base_acriss}`}
								         items={[{ key: 'fill', render: (close) => (
									         <FillAllItem unit={false} onApply={(price) => { onFillBase(price); close() }}/>
								         ) }]}/>
							</Stack>
							<Typography sx={{ fontSize: T.caption, color: dsColorNew.textMuted }}>
								floor, €/day
							</Typography>
						</TableCell>
						{bands.map((b, i) => (
							<TableCell key={b.from}>
								<Stack direction="row" alignItems="center" gap={0.75}>
									<TextField
										size="small" type="number" sx={{ ...inputSx, ...fieldH(), width: 88 }}
										inputProps={{ min: 0, step: '0.01',
										              'aria-label': `Floor of ${matrix.base_acriss}, ${bandLabel(b)}`,
										              style: { ...numberInput, textAlign: 'right' } }}
										value={matrix.base?.[i]?.price ?? ''}
										onChange={(e) => onBase(i, e.target.value === '' ? null : Number(e.target.value))}/>
									<Typography sx={{ fontSize: T.label, fontWeight: W.medium, color: dsColorNew.textMuted }}>
										€/day
									</Typography>
								</Stack>
							</TableCell>
						))}
					</TableRow>
					{(matrix.rules || []).map(rule => (
						<TableRow key={rule.acriss}>
							<TableCell sx={{ ...stickyCol() }}>
								<Stack direction="row" alignItems="center" gap={0.75}>
									<Code>{rule.acriss}</Code>
									<Box flex={1}/>
									<RowMenu label={`Shortcuts for ${rule.acriss}`} items={[
										{ key: 'eq', label: '= reference in every band',
										  onClick: () => onFillRule(rule.acriss, 0, 'euro') },
										{ key: 'fill', render: (close) => (
											<FillAllItem unit onApply={(value, unit) => {
												onFillRule(rule.acriss, value, unit); close()
											}}/>
										) },
										...(matrix.rules || [])
											.filter(r => r.acriss !== rule.acriss)
											.map(r => ({ key: `copy-${r.acriss}`, label: `Copy from ${r.acriss}`,
											             onClick: () => onCopyRule(rule.acriss, r.acriss) })),
									]}/>
								</Stack>
								<Typography sx={{ fontSize: T.caption, color: dsColorNew.textMuted }}>
									on {matrix.base_acriss}
								</Typography>
							</TableCell>
							{bands.map((b, i) => (
								<TableCell key={b.from} sx={{ verticalAlign: 'top' }}>
									<Stack direction="row" gap={0.75}>
										<TextField
											size="small" type="number" sx={{ ...inputSx, ...fieldH(), width: 76 }}
											inputProps={{ step: '0.01',
											              'aria-label': `Adjustment of ${rule.acriss}, ${bandLabel(b)}`,
											              style: { ...numberInput, textAlign: 'right' } }}
											value={rule.bands?.[i]?.value ?? ''}
											onChange={(e) => onRule(rule.acriss, i,
												{ value: e.target.value === '' ? null : Number(e.target.value) })}/>
										<Select
											size="small"
											sx={{ ...inputSx, width: 72, height: 38, fontSize: T.label, fontWeight: W.bold }}
											value={rule.bands?.[i]?.unit || 'euro'}
											inputProps={{ 'aria-label': `Unit of the adjustment of ${rule.acriss}, ${bandLabel(b)}` }}
											onChange={(e) => onRule(rule.acriss, i, { unit: e.target.value })}>
											<MenuItem value="euro" sx={{ fontSize: T.label }}>€/day</MenuItem>
											<MenuItem value="percent" sx={{ fontSize: T.label }}>%</MenuItem>
										</Select>
									</Stack>
									{/* A zero adjustment reads as "no price" at a glance. Say
									    what it means: this band costs what the reference does. */}
									{Number(rule.bands?.[i]?.value) === 0 && (
										<Typography sx={{ fontSize: T.caption, fontWeight: W.bold,
										                  color: dsColorNew.successText, mt: 0.5 }}>
											= reference
										</Typography>
									)}
								</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	)
}

/**
 * One value for every band of a row. A chip that opens a tiny popover
 * rather than an inline field: the row already has one field per band,
 * and a ninth one permanently on screen would read as another band.
 */
/** The row shortcuts, one ⋯ per row. An item is either a plain command
 *  or its own little form, which is why `render` gets the closer. */
function RowMenu({ label, items }) {
	const [anchor, setAnchor] = useState(null)
	const close = () => setAnchor(null)
	return (
		<>
			<IconButton size="small" aria-label={label} title="Shortcuts for this row"
			            onClick={(e) => setAnchor(e.currentTarget)}
			            sx={{ color: dsColorNew.textMuted }}>
				<MoreIcon fontSize="small"/>
			</IconButton>
			<Menu open={!!anchor} anchorEl={anchor} onClose={close}>
				{items.map(it => (it.render
					? <Box key={it.key} sx={{ px: 1.5, py: 1 }}>{it.render(close)}</Box>
					: (
						<MenuItem key={it.key} sx={{ fontSize: T.label, fontWeight: W.bold }}
						          onClick={() => { it.onClick(); close() }}>
							{it.label}
						</MenuItem>
					)))}
			</Menu>
		</>
	)
}

/** "Set every band to this" — a form, so it sits inside the menu rather
 *  than opening a second popover on top of it. */
function FillAllItem({ unit, onApply }) {
	const [value, setValue] = useState('')
	const [kind, setKind] = useState('euro')
	const apply = () => {
		if (value === '' || !Number.isFinite(Number(value))) return
		onApply(Number(value), kind)
	}
	return (
		<>
			<Typography sx={{ fontSize: T.caption, color: dsColorNew.textMuted, mb: 0.75 }}>
				Set every band to
			</Typography>
			<Stack direction="row" gap={1} alignItems="center">
					<TextField size="small" type="number" autoFocus value={value}
					           placeholder={unit ? '+5' : '40.00'}
					           onChange={(e) => setValue(e.target.value)}
					           onKeyDown={(e) => { if (e.key === 'Enter') apply() }}
					           inputProps={{ step: '0.01',
					                         'aria-label': unit ? 'Adjustment for every band' : 'Floor for every band',
					                         style: { ...numberInput, width: 96, textAlign: 'right' } }}
					           sx={{ ...inputSx, ...fieldH() }}/>
					{unit ? (
						<Select size="small" value={kind} onChange={(e) => setKind(e.target.value)}
						        inputProps={{ 'aria-label': 'Unit' }}
						        sx={{ ...inputSx, width: 96, height: 38, fontSize: T.label, fontWeight: W.bold }}>
							<MenuItem value="euro" sx={{ fontSize: T.label }}>€/day</MenuItem>
							<MenuItem value="percent" sx={{ fontSize: T.label }}>%</MenuItem>
						</Select>
					) : (
						<Typography sx={{ fontSize: T.label, fontWeight: W.medium, color: dsColorNew.textMuted }}>€/day</Typography>
					)}
				<AppBulkEditAction onClick={apply} disabled={value === ''} sx={{ height: 38 }}>
					Apply
				</AppBulkEditAction>
			</Stack>
		</>
	)
}

function PreviewTable({ rows, base, linked, onDayPrice }) {
	const codes = [base, ...linked].filter(Boolean)
	return (
		<TableContainer sx={{ border: '1px solid', borderColor: dsColorNew.grayBorder,
		                      borderRadius: 1.5, maxHeight: 560, overflow: 'auto' }}>
			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow>
						<Th sx={{ width: 104, ...stickyCol(dsColorNew.tableHead), zIndex: 3 }}>Rental days</Th>
						{codes.map(c => (
							<Th key={c} align="right">{c} · €/day</Th>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map(row => (
						<TableRow key={row.lor}
						          sx={{ '&:nth-of-type(even)': { bgcolor: dsColorNew.tableRowAlt },
						                '&:hover': { bgcolor: dsColorNew.hoverBgLight } }}>
							{/* `inherit` so the zebra stripe and the hover show through
							    the sticky cell instead of a white stripe down the side. */}
							<TableCell sx={{ fontWeight: W.heavy, fontSize: T.body, color: dsColorNew.appBlue,
							                 fontVariantNumeric: 'tabular-nums', py: 1,
							                 ...stickyCol('inherit') }}>
								{String(row.lor).padStart(2, '0')}
							</TableCell>
							{codes.map(c => {
								const v = row.prices[c]
								if (c === base) {
									return (
										<TableCell key={c} align="right" sx={{ py: 0.75 }}>
											<TextField
												size="small" type="number"
												value={v ?? ''}
												placeholder="no floor"
												onChange={(e) => onDayPrice(row.lor,
													e.target.value === '' ? null : Number(e.target.value))}
												inputProps={{ min: 0, step: '0.01',
												              'aria-label': `Floor for ${base} at ${row.lor} rental days`,
												              style: { ...numberInput, width: 84, textAlign: 'right' } }}
												sx={{ ...inputSx, ...fieldH(34) }}/>
										</TableCell>
									)
								}
								return (
									<TableCell key={c} align="right"
									           sx={{ py: 1, fontSize: T.body, fontWeight: W.bold,
									                 fontVariantNumeric: 'tabular-nums',
									                 color: v == null ? dsColorNew.textSubtle
									                      : v < 0 ? dsColorNew.dangerRed : dsColorNew.textTitle }}>
										{v == null ? 'no floor' : money(v)}
									</TableCell>
								)
							})}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	)
}

// ─── Band editor ─────────────────────────────────────────────────────────

/**
 * The whole 1–30 continuum, as thirty buttons.
 *
 * Click a day and a new band starts there; click the first day of a
 * band and it merges into the one before. One gesture, discrete, no
 * dragging — so it works from the keyboard and needs no second
 * mechanism (WCAG 2.2, dragging movements). The typed inputs underneath
 * are the same partition seen the other way round, kept in sync, for
 * the operator who already knows the numbers.
 *
 * Edits live in a draft until Apply: re-cutting the grid is the one
 * change on this screen that touches every cell, so it should not
 * happen on every click.
 */
// One soft tone of the palette per band, in turn
const BAND_TINTS = [dsColorNew.pillActiveBg, dsColorNew.successBg, dsColorNew.noteAmberBg, alpha(dsColorNew.cyan, 0.12), dsColorNew.noteInfoBg, dsColorNew.pillTrackBg]
const BAND_INKS  = [dsColorNew.appBlue, dsColorNew.successText, dsColorNew.noteAmber, dsColorNew.cyanHover, dsColorNew.noteInfo, dsColorNew.textDark]

function BandEditorDialog({ open, bands, onClose, onApply }) {
	const [cuts, setCutsDraft] = useState(cutsFromBands(bands))
	useEffect(() => { if (open) setCutsDraft(cutsFromBands(bands)) }, [open])

	const draft = bandsFromCuts(cuts)
	const bandOf = (day) => draft.findIndex(b => day >= b.from && (b.to == null || day <= b.to))
	const isStart = (day) => draft.some(b => b.from === day)

	const toggleDay = (day) => {
		if (day === 1) return
		if (isStart(day)) setCutsDraft(cuts.filter(c => c !== day - 1))   // merge into the previous
		else setCutsDraft([...cuts, day - 1])                             // split here
	}

	const setEnd = (i, day) => setCutsDraft(cuts.map((c, j) => (j === i ? day : c)))
	const tint = (i) => BAND_TINTS[i % BAND_TINTS.length]
	const ink  = (i) => BAND_INKS[i % BAND_INKS.length]

	return (
		<AppDialog open={open} onClose={onClose} maxWidth="md" paperSx={setupScopeSx}>
			<AppDialogHeader icon="ruler-horizontal" title="Edit length-of-rental bands" onClose={onClose}/>
			<AppDialogBody sx={dialogBodySx}>
				<Stack gap={3} sx={{ pt: 0.5 }}>
					<Box>
						<Label>Quick picks</Label>
						<Stack direction="row" gap={0.75} flexWrap="wrap">
							{QUICK_PICKS.map(q => {
								const on = JSON.stringify(bandsFromCuts(q.cuts)) === JSON.stringify(draft)
								return (
									<AppFilterChip key={q.label} label={q.label} selected={on}
									               onClick={() => setCutsDraft(q.cuts)}/>
								)
							})}
						</Stack>
					</Box>

					<Box>
						<Note sx={{ mb: 1.5, fontSize: T.body }}>
							Each cell is a length of rental in days, not a date. Click one to start a new
							band there; click the first of a band to merge it into the one before.
						</Note>
						<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(16, minmax(0, 1fr))', gap: 0.5 }}>
							{Array.from({ length: PREVIEW_DAYS }, (_, i) => i + 1).map(day => {
								const k = bandOf(day)
								const start = isStart(day)
								return (
									<Box key={day} component="button" type="button"
									     onClick={() => toggleDay(day)}
									     disabled={day === 1}
									     aria-label={start && day > 1
										     ? `${day} rental days: starts band ${bandLabel(draft[k])}. Click to merge it into the band before.`
										     : `${day} rental days, in band ${bandLabel(draft[k])}. Click to start a new band here.`}
									     sx={{
										     appearance: 'none', cursor: day === 1 ? 'default' : 'pointer',
										     height: 44, border: '1px solid', borderRadius: 1,
										     fontSize: T.body, fontWeight: start ? W.heavy : W.medium,
										     fontVariantNumeric: 'tabular-nums',
										     bgcolor: tint(k), color: ink(k),
										     borderColor: start ? ink(k) : 'transparent',
										     borderLeftWidth: start && day > 1 ? 3 : 1,
										     '&:hover': { filter: day === 1 ? 'none' : 'brightness(0.95)' },
										     '&:focus-visible': { outline: '2px solid', outlineColor: dsColorNew.appBlue, outlineOffset: 1 },
									     }}>
										{day}
									</Box>
								)
							})}
							<Box sx={{ gridColumn: 'span 2', height: 44, border: '1px solid',
							           borderColor: ink(draft.length - 1), borderLeftWidth: 3, borderRadius: 1,
							           bgcolor: tint(draft.length - 1), color: ink(draft.length - 1),
							           display: 'grid', placeItems: 'center',
							           fontSize: T.body, fontWeight: W.heavy }}>
								{draft[draft.length - 1].from}+
							</Box>
						</Box>
						<Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1.5 }}>
							{draft.map((b, i) => (
								<Chip key={b.from} size="small" label={bandLabel(b)}
								      sx={{ fontWeight: W.bold, fontSize: T.label, height: 28,
								            bgcolor: tint(i), color: ink(i) }}/>
							))}
						</Stack>
					</Box>

					<Box>
						<Label>Bands</Label>
						<Stack gap={1}>
							{draft.map((b, i) => (
								<Stack key={b.from} direction="row" alignItems="center" gap={1.5}>
									<Box sx={{ width: 130 }}>
										<Typography sx={{ fontSize: T.body, fontWeight: W.bold }}>{bandLabel(b)}</Typography>
									</Box>
									{b.to != null ? (
										<>
											<Note sx={{ fontSize: T.body }}>ends at</Note>
											<TextField size="small" type="number"
											           value={b.to}
											           onChange={(e) => setEnd(i, Number(e.target.value))}
											           inputProps={{ min: b.from, max: PREVIEW_DAYS,
											                         'aria-label': `Last rental length of the band starting at ${b.from} days`,
											                         style: { ...numberInput, width: 60, textAlign: 'center' } }}
											           sx={{ ...inputSx, ...fieldH() }}/>
										</>
									) : (
										<Note sx={{ fontSize: T.body }}>open — the monthly price, {b.from} days and longer</Note>
									)}
								</Stack>
							))}
						</Stack>
					</Box>
				</Stack>
			</AppDialogBody>
			<AppDialogFooter>
				<AppDialogCancel onClick={onClose}/>
				<AppBulkEditAction onClick={() => onApply(cuts)}>
					Apply
				</AppBulkEditAction>
			</AppDialogFooter>
		</AppDialog>
	)
}
